import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Send } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactCTA() {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    website: "", // honeypot
  });
  const [submitting, setSubmitting] = useState(false);

  // Newsletter state
  const [nlEmail, setNlEmail] = useState("");
  const [nlSubmitting, setNlSubmitting] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        message: form.message.trim(),
        website: form.website,
      });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", company: "", message: "", website: "" });
    } catch (err) {
      console.error(err);
      const code = err?.response?.status;
      if (code === 429) toast.error(t.contact.rateLimit);
      else toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  const onNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (nlSubmitting) return;
    setNlSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email: nlEmail.trim() });
      toast.success(t.newsletter.success);
      setNlEmail("");
    } catch (err) {
      console.error(err);
      const code = err?.response?.status;
      if (code === 429) toast.error(t.contact.rateLimit);
      else toast.error(t.newsletter.error);
    } finally {
      setNlSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#0A0A0A] text-[#FAFAFA] py-24 md:py-32 relative noise-overlay border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 relative">
        <div className="lg:col-span-6">
          <p className="overline">{t.contact.overline}</p>
          <h2
            data-testid="contact-headline"
            className="font-display uppercase mt-6 text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95]"
          >
            <span className="block text-white">{t.contact.titleA}</span>
            <span className="block text-white">{t.contact.titleB}</span>
            <span className="block text-white relative">
              {t.contact.titleC}
              <span className="block w-20 h-[3px] bg-redflag mt-3" />
            </span>
          </h2>
          <p className="mt-8 text-base md:text-lg text-white/70 leading-relaxed max-w-md">
            {t.contact.blurb}
          </p>

          {/* Newsletter inline card */}
          <div className="mt-10 border border-white/10 p-6 bg-[#0F0F0F]">
            <div className="font-display uppercase text-xl text-white">
              {t.newsletter.title}
            </div>
            <p className="mt-2 text-sm text-white/55">
              {t.newsletter.subtitle}
            </p>
            <form
              onSubmit={onNewsletterSubmit}
              data-testid="newsletter-form"
              className="mt-5 flex flex-col sm:flex-row gap-3"
            >
              <input
                type="email"
                required
                value={nlEmail}
                onChange={(e) => setNlEmail(e.target.value)}
                placeholder={t.newsletter.placeholder}
                data-testid="newsletter-input"
                className="flex-1 bg-[#0A0A0A] border border-white/15 focus:border-redflag px-4 py-3 text-sm outline-none transition-colors text-white placeholder:text-white/35"
              />
              <button
                type="submit"
                disabled={nlSubmitting}
                data-testid="newsletter-submit"
                className="btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium uppercase tracking-wide disabled:opacity-60"
              >
                <Send size={14} />
                {nlSubmitting ? "…" : t.newsletter.cta}
              </button>
            </form>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="lg:col-span-6 space-y-6 relative"
        >
          {/* Honeypot — hidden field bots will fill */}
          <div className="hp-field" aria-hidden="true">
            <label htmlFor="contact-website">Website</label>
            <input
              id="contact-website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={update("website")}
              data-testid="contact-honeypot"
            />
          </div>

          <div>
            <label
              htmlFor="contact-name"
              className="overline text-white/55 block mb-2"
            >
              {t.contact.name}
            </label>
            <input
              id="contact-name"
              data-testid="contact-input-name"
              type="text"
              required
              minLength={2}
              value={form.name}
              onChange={update("name")}
              placeholder={t.contact.placeholders.name}
              className="w-full bg-transparent border-b border-white/25 focus:border-redflag outline-none py-3 text-lg placeholder:text-white/25 transition-colors text-white"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="contact-email"
                className="overline text-white/55 block mb-2"
              >
                {t.contact.email}
              </label>
              <input
                id="contact-email"
                data-testid="contact-input-email"
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                placeholder={t.contact.placeholders.email}
                className="w-full bg-transparent border-b border-white/25 focus:border-redflag outline-none py-3 text-lg placeholder:text-white/25 transition-colors text-white"
              />
            </div>
            <div>
              <label
                htmlFor="contact-company"
                className="overline text-white/55 block mb-2"
              >
                {t.contact.company}
              </label>
              <input
                id="contact-company"
                data-testid="contact-input-company"
                type="text"
                value={form.company}
                onChange={update("company")}
                placeholder={t.contact.placeholders.company}
                className="w-full bg-transparent border-b border-white/25 focus:border-redflag outline-none py-3 text-lg placeholder:text-white/25 transition-colors text-white"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="contact-message"
              className="overline text-white/55 block mb-2"
            >
              {t.contact.message}
            </label>
            <textarea
              id="contact-message"
              data-testid="contact-input-message"
              rows={4}
              required
              minLength={5}
              value={form.message}
              onChange={update("message")}
              placeholder={t.contact.placeholders.message}
              className="w-full bg-transparent border-b border-white/25 focus:border-redflag outline-none py-3 text-lg placeholder:text-white/25 transition-colors resize-none text-white"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit-button"
            className="btn-primary group inline-flex items-center gap-2 px-7 py-4 font-medium uppercase tracking-wide text-sm disabled:opacity-60"
          >
            {submitting ? t.contact.submitting : t.contact.submit}
            <ArrowUpRight
              size={18}
              className="group-hover:rotate-45 transition-transform duration-300"
            />
          </button>
        </form>
      </div>
    </section>
  );
}
